import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import { deleteLandingPageLibraryItem, getLandingPageMediaLibrary } from '../../services/api';
import type { LandingMediaLibraryItem, LandingMediaLibraryKind } from '../../services/api';
import { PUBLIC_APP_ORIGIN } from '../../config/api';

/** Cùng MIME với preview iframe (public-landing.html) — kéo thumbnail vào canvas. */
const LP_LIBRARY_DRAG_MIME = 'application/x-mphoto-landing-library';

function absAssetUrl(path: string) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${PUBLIC_APP_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

function fileKindFromFile(f: File): LandingMediaLibraryKind {
  if (f.type.startsWith('video/')) return 'video';
  if (f.type === 'image/gif') return 'gif';
  return 'image';
}

type LibraryApplyOptions = { alwaysAdd?: boolean };

export type LandingLibraryPickMode = 'applyOnClick' | 'multiSelect';

type Props = {
  disabled?: boolean;
  uploadAsset: (file: File) => Promise<string | null>;
  onApply: (url: string, kind: LandingMediaLibraryKind, opts?: LibraryApplyOptions) => void | Promise<void>;
  /** Chọn nhiều rồi bấm «Thêm» — không gọi onApply khi bấm từng ô. */
  pickMode?: LandingLibraryPickMode;
  onApplyMulti?: (items: Array<{ url: string; kind: LandingMediaLibraryKind }>) => void | Promise<void>;
  /**
   * Khi kéo thumbnail rồi thả chuột: gọi với tọa độ màn hình (Edge/iframe thường không có drop HTML5 tin cậy từ parent).
   * Parent kiểm tra có nằm trên preview iframe không rồi thêm khối.
   */
  onDragReleaseOverPreview?: (info: {
    clientX: number;
    clientY: number;
    url: string;
    kind: LandingMediaLibraryKind;
  }) => void;
  /** Bật/tắt theo dõi con trỏ trong iframe preview (Edge) — gọi true ở dragstart, false ở dragend. */
  onLibraryDragArmChange?: (armed: boolean) => void;
  /** Đồng bộ tab với loại phần tử đang chọn (khi chỉnh khối media). Panel Thư viện riêng đặt false. */
  alignTabToMediaKind?: boolean;
  /** Chỉ dùng khi alignTabToMediaKind — không ảnh hưởng tab khi alignTabToMediaKind=false */
  mediaKind: 'image' | 'video';
};

const LandingMediaLibraryPicker: React.FC<Props> = ({
  disabled,
  uploadAsset,
  onApply,
  onApplyMulti,
  pickMode = 'applyOnClick',
  onDragReleaseOverPreview,
  onLibraryDragArmChange,
  alignTabToMediaKind = true,
  mediaKind,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  /** Kéo thư viện → preview: dragend thường cho (0,0) khi thả trên iframe khác origin — cập nhật lastX/Y bằng sự kiện `drag` trên window. */
  const libraryCanvasDragRef = useRef<{
    url: string;
    kind: LandingMediaLibraryKind;
    lastX: number;
    lastY: number;
  } | null>(null);
  const winDragListenerRef = useRef<((ev: DragEvent) => void) | null>(null);

  const removeLibraryWinDragListener = useCallback(() => {
    const fn = winDragListenerRef.current;
    if (fn) {
      window.removeEventListener('drag', fn, true);
      winDragListenerRef.current = null;
    }
  }, []);

  useEffect(() => () => removeLibraryWinDragListener(), [removeLibraryWinDragListener]);
  const [tab, setTab] = useState(0);
  const [items, setItems] = useState<LandingMediaLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const [batchUploading, setBatchUploading] = useState(false);
  const [deletingFilename, setDeletingFilename] = useState<string | null>(null);
  const dragDepthRef = useRef(0);
  /** Thứ tự chọn (multiSelect) — theo `filename`. */
  const [multiSelectedFilenames, setMultiSelectedFilenames] = useState<string[]>([]);

  const load = useCallback(async (): Promise<LandingMediaLibraryItem[]> => {
    setLoading(true);
    setListError(null);
    try {
      const res = await getLandingPageMediaLibrary();
      if (res.success && Array.isArray(res.items)) {
        setItems(res.items);
        return res.items;
      }
      setItems([]);
      const raw = res as unknown as { error?: string };
      const msg =
        typeof raw.error === 'string' ? raw.error : 'Không tải được danh sách thư viện';
      setListError(msg);
      toast.error(msg);
      return [];
    } catch (e: unknown) {
      setItems([]);
      let msg = 'Không đọc được thư viện (mạng hoặc phiên đăng nhập).';
      if (axios.isAxiosError(e)) {
        const d = e.response?.data as { error?: string } | undefined;
        if (typeof d?.error === 'string') msg = d.error;
        else if (e.response?.status === 401) msg = 'Hết phiên đăng nhập — tải lại trang và đăng nhập lại.';
      }
      setListError(msg);
      toast.error(msg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!alignTabToMediaKind) return;
    setTab(mediaKind === 'video' ? 1 : 0);
  }, [alignTabToMediaKind, mediaKind]);

  useEffect(() => {
    if (pickMode !== 'multiSelect') setMultiSelectedFilenames([]);
  }, [pickMode]);

  const uploadOne = useCallback(
    async (f: File, applyToCanvas: boolean) => {
      if (disabled) return;
      const url = await uploadAsset(f);
      if (!url) return;
      const list = await load();
      const applyNow = applyToCanvas && pickMode !== 'multiSelect';
      if (applyNow) await Promise.resolve(onApply(url, fileKindFromFile(f)));
      else if (pickMode === 'multiSelect') {
        const k = fileKindFromFile(f);
        if (k === 'video') return;
        const uNorm = absAssetUrl(url.trim());
        const match = list.find(
          (it) =>
            (it.kind === 'image' || it.kind === 'gif') &&
            (absAssetUrl(it.url) === uNorm || it.url === url.trim()),
        );
        if (match) {
          setMultiSelectedFilenames((prev) =>
            prev.includes(match.filename) ? prev : [...prev, match.filename],
          );
        }
      }
    },
    [disabled, load, onApply, pickMode, uploadAsset],
  );

  const handleFiles = useCallback(
    async (f: File | null) => {
      if (!f || disabled) return;
      await uploadOne(f, true);
    },
    [disabled, uploadOne],
  );

  const handleDroppedOrPickedFiles = useCallback(
    async (fileList: FileList | File[] | null) => {
      if (!fileList || disabled) return;
      const arr = Array.from(fileList).filter(
        (f) => f.type.startsWith('image/') || f.type.startsWith('video/'),
      );
      if (!arr.length) return;
      if (arr.length === 1) {
        await uploadOne(arr[0], true);
        return;
      }
      setBatchUploading(true);
      try {
        for (let i = 0; i < arr.length; i++) {
          await uploadAsset(arr[i]);
        }
        await load();
      } finally {
        setBatchUploading(false);
      }
    },
    [disabled, load, uploadAsset, uploadOne],
  );

  useEffect(() => {
    const onPaste = (e: Event) => {
      const ev = e as ClipboardEvent;
      const el = ev.target as HTMLElement | null;
      if (el?.closest?.('input, textarea, [contenteditable="true"]')) return;
      const dt = ev.clipboardData;
      if (!dt) return;
      const files = dt.files;
      if (files && files.length) {
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          if (f.type.startsWith('image/') || f.type.startsWith('video/')) {
            e.preventDefault();
            void handleFiles(f);
            return;
          }
        }
      }
      const itemsList = dt.items;
      for (let i = 0; i < itemsList.length; i++) {
        const it = itemsList[i];
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f && (f.type.startsWith('image/') || f.type.startsWith('video/'))) {
            e.preventDefault();
            void handleFiles(f);
            return;
          }
        }
      }
    };
    window.addEventListener('paste', onPaste as EventListener);
    return () => window.removeEventListener('paste', onPaste as EventListener);
  }, [handleFiles]);

  const filtered = useMemo(() => {
    const want: LandingMediaLibraryKind =
      tab === 0 ? 'image' : tab === 1 ? 'video' : 'gif';
    return items.filter((it) => it.kind === want);
  }, [items, tab]);

  const busy = disabled || batchUploading;

  const pickFromLibrary = async (url: string, kind: LandingMediaLibraryKind) => {
    if (busy || pickMode === 'multiSelect') return;
    /** Luôn thêm khối mới khi chọn từ lưới thư viện (không ghi đè khối media đang chọn). */
    await Promise.resolve(onApply(url, kind, { alwaysAdd: true }));
  };

  const toggleMultiSelect = useCallback((filename: string, kind: LandingMediaLibraryKind) => {
    if (busy || pickMode !== 'multiSelect') return;
    if (kind === 'video') {
      toast.info('Dải ảnh chỉ nhận ảnh hoặc GIF — hãy chọn tab Ảnh / GIF.');
      return;
    }
    setMultiSelectedFilenames((prev) => {
      const i = prev.indexOf(filename);
      if (i >= 0) return prev.filter((_, j) => j !== i);
      return [...prev, filename];
    });
  }, [busy, pickMode]);

  const commitMultiSelect = useCallback(async () => {
    if (busy || pickMode !== 'multiSelect' || !onApplyMulti || !multiSelectedFilenames.length) return;
    const rows = multiSelectedFilenames
      .map((fn) => items.find((it) => it.filename === fn))
      .filter((x): x is LandingMediaLibraryItem => Boolean(x));
    const payload = rows.map((it) => ({ url: it.url, kind: it.kind }));
    await Promise.resolve(onApplyMulti(payload));
    setMultiSelectedFilenames([]);
  }, [busy, items, multiSelectedFilenames, onApplyMulti, pickMode]);

  const handleDeleteItem = useCallback(
    async (filename: string) => {
      if (busy || deletingFilename) return;
      if (!window.confirm('Xóa file này khỏi thư viện? Không thể hoàn tác.')) return;
      setDeletingFilename(filename);
      try {
        const res = await deleteLandingPageLibraryItem(filename);
        if (res.success) {
          toast.success('Đã xóa file');
          await load();
        } else {
          toast.error(res.error || 'Không xóa được');
        }
      } catch (e: unknown) {
        let msg = 'Không xóa được file';
        if (axios.isAxiosError(e)) {
          const d = e.response?.data as { error?: string } | undefined;
          if (typeof d?.error === 'string') msg = d.error;
        }
        toast.error(msg);
      } finally {
        setDeletingFilename(null);
      }
    },
    [busy, deletingFilename, load],
  );

  const wrongTabHint =
    !loading &&
    !batchUploading &&
    items.length > 0 &&
    filtered.length === 0;

  return (
    <Stack spacing={1.25} sx={{ width: '100%' }}>
      <input
        ref={fileRef}
        type="file"
        hidden
        multiple
        accept="image/*,video/*"
        onChange={(e) => {
          void handleDroppedOrPickedFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <Button
        fullWidth
        variant="contained"
        size="medium"
        startIcon={<CloudUploadIcon />}
        disabled={busy}
        onClick={() => fileRef.current?.click()}
        sx={{ py: 1, fontWeight: 600 }}
      >
        Tải lên
      </Button>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          disabled={busy || loading}
          onClick={() => void load()}
        >
          Làm mới
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {pickMode === 'multiSelect' ? (
          <>
            Bấm từng ảnh / GIF để chọn (bấm lại để bỏ chọn), rồi bấm <strong>Thêm</strong>. Video không dùng
            cho dải ảnh. Kéo thumbnail thả lên preview vẫn dùng được như bình thường.
          </>
        ) : (
          <>
            Kéo thả file vào khung bên dưới hoặc dán Ctrl+V (trừ khi đang gõ trong ô chữ). Kéo thumbnail thả lên
            vùng preview bên phải để thêm tại vị trí thả (Edge có thể vẫn hiện «cấm» khi kéo — cứ thả trên preview).
            Nhiều file: tải hết lên thư viện; một file: thêm luôn vào trang nếu đang chọn khu vực. Lưu Firebase + metadata DB, URL CDN khi bật rewrite
            ngay — không cần bấm Lưu cấu hình.
          </>
        )}
      </Typography>

      {listError && (
        <Alert severity="warning" onClose={() => setListError(null)} sx={{ py: 0.5 }}>
          {listError}
        </Alert>
      )}

      <Box
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          dragDepthRef.current += 1;
          setDropActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          dragDepthRef.current -= 1;
          if (dragDepthRef.current <= 0) {
            dragDepthRef.current = 0;
            setDropActive(false);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          dragDepthRef.current = 0;
          setDropActive(false);
          void handleDroppedOrPickedFiles(e.dataTransfer.files);
        }}
        sx={{
          borderRadius: 1,
          outline: dropActive ? '2px dashed' : '1px solid',
          outlineColor: dropActive ? 'primary.main' : 'divider',
          outlineOffset: 0,
          bgcolor: dropActive ? 'rgba(99, 102, 241, 0.08)' : 'background.paper',
          transition: 'outline-color 0.15s, background-color 0.15s',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 36,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 13 },
          }}
        >
          <Tab label="Ảnh" />
          <Tab label="Video" />
          <Tab label="GIF" />
        </Tabs>

        {dropActive && (
          <Typography
            variant="caption"
            color="primary"
            sx={{ display: 'block', textAlign: 'center', py: 0.75, bgcolor: 'rgba(99, 102, 241, 0.12)' }}
          >
            Thả file để tải lên thư viện…
          </Typography>
        )}

      <Box
        sx={{
          p: 1,
          minHeight: 160,
          maxHeight: 280,
          overflow: 'auto',
          bgcolor: dropActive ? 'rgba(99, 102, 241, 0.06)' : 'action.hover',
        }}
      >
        {loading || batchUploading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }} spacing={1}>
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary">
              {batchUploading ? 'Đang tải lên…' : 'Đang tải danh sách thư viện…'}
            </Typography>
          </Stack>
        ) : wrongTabHint ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, px: 1, textAlign: 'center' }}>
            Thư viện đã có {items.length} file — đổi tab <strong>Ảnh</strong>, <strong>Video</strong> hoặc <strong>GIF</strong> để xem đúng loại (ví dụ video nằm ở tab Video, GIF ở tab GIF).
          </Typography>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, px: 1, textAlign: 'center' }}>
            Chưa có file trong mục này. Bấm <strong>Tải lên</strong>, hoặc kéo thả ảnh / video / GIF vào khung này.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
              gap: 0.75,
            }}
          >
            {filtered.map((it) => {
              const src = absAssetUrl(it.url);
              const itemBusy = Boolean(deletingFilename === it.filename);
              const multiOn =
                pickMode === 'multiSelect' &&
                it.kind !== 'video' &&
                multiSelectedFilenames.includes(it.filename);
              return (
                <Box
                  key={it.filename}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '100%',
                    border: 1,
                    borderColor: multiOn ? 'primary.main' : 'divider',
                    borderWidth: multiOn ? 2 : 1,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                    opacity: itemBusy ? 0.65 : 1,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'action.hover',
                    }}
                  >
                    {it.kind === 'video' ? (
                      <video
                        src={src}
                        muted
                        playsInline
                        preload="metadata"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <img
                        src={src}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        draggable={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    draggable={false}
                    disabled={busy || itemBusy}
                    aria-label="Xóa khỏi thư viện"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      void handleDeleteItem(it.filename);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      zIndex: 2,
                      color: 'common.white',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      width: 28,
                      height: 28,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Box
                    component="button"
                    type="button"
                    disabled={busy || itemBusy}
                    draggable={!(busy || itemBusy)}
                    onDragStart={(e) => {
                      if (busy || itemBusy) return;
                      onLibraryDragArmChange?.(true);
                      removeLibraryWinDragListener();
                      const lx = Number.isFinite(e.clientX) ? e.clientX : 0;
                      const ly = Number.isFinite(e.clientY) ? e.clientY : 0;
                      libraryCanvasDragRef.current = {
                        url: it.url,
                        kind: it.kind,
                        lastX: lx,
                        lastY: ly,
                      };
                      const onWinDrag = (ev: DragEvent) => {
                        const cur = libraryCanvasDragRef.current;
                        if (!cur) return;
                        if (Number.isFinite(ev.clientX) && Number.isFinite(ev.clientY)) {
                          cur.lastX = ev.clientX;
                          cur.lastY = ev.clientY;
                        }
                      };
                      winDragListenerRef.current = onWinDrag;
                      window.addEventListener('drag', onWinDrag, true);
                      try {
                        const json = JSON.stringify({ url: it.url, kind: it.kind });
                        e.dataTransfer.setData(LP_LIBRARY_DRAG_MIME, json);
                        e.dataTransfer.setData('text/plain', `mphoto-lp-lib:${json}`);
                        e.dataTransfer.effectAllowed = 'copy';
                      } catch {
                        /* ignore */
                      }
                    }}
                    onDragEnd={(e) => {
                      removeLibraryWinDragListener();
                      const p = libraryCanvasDragRef.current;
                      libraryCanvasDragRef.current = null;
                      try {
                        if (!p || busy || itemBusy) return;
                        let cx = e.clientX;
                        let cy = e.clientY;
                        const finite = Number.isFinite(cx) && Number.isFinite(cy);
                        const looksLikeBrokenDragEnd =
                          !finite || (Math.abs(cx) < 0.5 && Math.abs(cy) < 0.5);
                        if (looksLikeBrokenDragEnd) {
                          cx = p.lastX;
                          cy = p.lastY;
                        } else {
                          const vw = window.innerWidth;
                          const vh = window.innerHeight;
                          if (cx < -4 || cy < -4 || cx > vw + 4 || cy > vh + 4) {
                            cx = p.lastX;
                            cy = p.lastY;
                          }
                        }
                        onDragReleaseOverPreview?.({
                          clientX: cx,
                          clientY: cy,
                          url: p.url,
                          kind: p.kind,
                        });
                      } finally {
                        onLibraryDragArmChange?.(false);
                      }
                    }}
                    onClick={() =>
                      pickMode === 'multiSelect'
                        ? toggleMultiSelect(it.filename, it.kind)
                        : void pickFromLibrary(it.url, it.kind)
                    }
                    aria-label={
                      pickMode === 'multiSelect'
                        ? `Chọn hoặc bỏ chọn ${it.filename}`
                        : `Chọn ${it.filename}`
                    }
                    aria-pressed={pickMode === 'multiSelect' ? multiOn : undefined}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 1,
                      m: 0,
                      p: 0,
                      border: 'none',
                      borderRadius: 1,
                      bgcolor: 'transparent',
                      cursor: busy || itemBusy ? 'default' : pickMode === 'multiSelect' ? 'pointer' : 'grab',
                      transition: 'box-shadow 0.15s, outline-color 0.15s',
                      '&:hover:not(:disabled)': {
                        boxShadow: (t) =>
                          pickMode === 'multiSelect' && multiOn
                            ? `inset 0 0 0 2px ${t.palette.primary.dark}`
                            : `inset 0 0 0 2px ${t.palette.primary.main}`,
                      },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: -2,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      </Box>

      {pickMode === 'multiSelect' && multiSelectedFilenames.length > 0 && (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          disabled={busy}
          onClick={() => void commitMultiSelect()}
          sx={{ py: 1.1, fontWeight: 700 }}
        >
          Thêm ({multiSelectedFilenames.length})
        </Button>
      )}
    </Stack>
  );
};

export default LandingMediaLibraryPicker;
